<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  /**
   * Function to fetch the list of items available for deletion.
   * @type {Function | null}
   * @default null
   */
  export let getFunction = null;

  /**
   * Function to execute the delete operation for the selected item.
   * @type {Function | null}
   * @default null
   */
  export let deleteFunction = null;

  /**
   * Function to rollback the delete operation.
   * @type {Function | null}
   * @default null
   */
  export let rollbackFunction = null;

  /**
   * Controls whether to display a preview of the selected item.
   * @type {boolean}
   * @default false
   */
  export let showPreview = false;

  let loading = true;
  let items = [];
  let selectedItem = null;

  onMount(async () => {
    items = await getFunction();

    loading = false;

    if (onExecute) {
      onExecute(executeDelete);
    }

    if (onRollback && rollbackFunction) {
      onRollback(rollbackFunction);
    }
  });

  const onSelectItem = (event) => {
    selectedItem = event.detail;
  };

  const executeDelete = async () => {
    await deleteFunction(selectedItem);
  };

  $: step.data = selectedItem ? { ...selectedItem } : {};

  $: nextDisabled = !selectedItem;
</script>

<div class="general-delete-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <SectionItem label={$format('label.SELECT_ITEM')} description={$format('label.CHOOSE_ITEM_TO_DELETE')}>
      <Select
        bind:items
        bind:value={selectedItem}
        placeholder="Select an item..."
        clearable={false}
        showChevron
        on:change={onSelectItem}
      />
    </SectionItem>

    {#if showPreview && selectedItem}
      <SectionItem label={$format('label.PREVIEW')}>
        <div class="item-preview">
          <slot name="preview" item={selectedItem}>
            <div class="default-preview">
              <strong>{selectedItem.label || selectedItem.value}</strong>
            </div>
          </slot>
        </div>
      </SectionItem>
    {/if}
  {/if}
</div>

<style lang="scss">
  .general-delete-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }

    .item-preview {
      background-color: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      padding: var(--padding-base);
    }

    .default-preview {
      padding: var(--padding-small);
    }
  }
</style>
